import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  SimpleGrid,
  Text,
  Button,
  Badge,
  VStack,
  HStack,
  Input,
  Textarea,
  Icon,
  Flex,
  Divider,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  FormControl,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/react';

import { DownloadIcon, CloseIcon, TimeIcon } from '@chakra-ui/icons';
import { HiUpload } from 'react-icons/hi';

import SaveButton from './SaveButton';
import LoadListingsScheduleModal from './LoadListingsScheduleModal';
import { useColors } from './colors';
import RunProgressPanel from 'components/run-progress/RunProgressPanel';
import { mapRunStepsToUi } from 'components/run-progress/mapRunStepsToUi';
import socketLoadListings from 'app/socketLoadListings';

import {
  uploadDescriptionTemplateApi,
  loadListingsApi,
  getLoadListingsStatusApi,
  updateLoadListingsScheduleApi,
  downloadLoadListingsRunReportCsvApi,
} from 'features/stores/stores.api';
import { setLoadListingsProcessStatusFromSocket } from 'features/stores/stores.slice';

const LOAD_LISTINGS_SUMMARY_STATS = [
  { key: 'totalListingsFound', label: 'Found' },
  { key: 'totalListingsAdded', label: 'Added', color: 'green.500' },
  { key: 'totalListingsSkipped', label: 'Skipped', color: 'orange.500' },
  { key: 'totalListingsDeleted', label: 'Deleted', color: 'red.500' },
  { key: 'duplicatesFound', label: 'Duplicates' },
];

const Card = ({ children, title, rightElement }) => {
  const { cardBgColor } = useColors();

  return (
    <Box bg={cardBgColor} borderRadius="xl" p={6} position="relative">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="semibold">
          {title}
        </Text>
        {rightElement}
      </Flex>
      {children}
    </Box>
  );
};

const ListingOptionsCard = ({ storeData, onStoreUpdate }) => {
  const dispatch = useDispatch();
  const {
    loadListingsStatus,
    loadListingsProcessStatus,
    isLoadListingsRunning,
    downloadLoadListingsReportStatus,
    updateLoadListingsScheduleStatus,
  } = useSelector((state) => state.stores);

  const [selectedFile, setSelectedFile] = useState(null);
  const [amazonSellerId, setAmazonSellerId] = useState('');
  const [amazonMarketplaceId, setAmazonMarketplaceId] = useState('');
  const {
    isOpen: isScheduleModalOpen,
    onOpen: onScheduleModalOpen,
    onClose: onScheduleModalClose,
  } = useDisclosure();
  const {
    textColor,
    inputColor,
    inputBgColor,
    borderColor,
    placeholderColor,
  } = useColors();

  const storeId = storeData?._id;

  useEffect(() => {
    if (storeData) {
      setAmazonSellerId(storeData.marketplace?.configuration?.sellerId || '');
      setAmazonMarketplaceId(storeData.marketplace?.configuration?.marketplaceId || '');
    }
  }, [storeData]);

  useEffect(() => {
    if (!storeId) return;
    dispatch(getLoadListingsStatusApi(storeId));
  }, [dispatch, storeId]);

  useEffect(() => {
    if (!storeId) return undefined;

    socketLoadListings.connect();
    const onRunState = (payload) => {
      if (payload?.storeId && payload.storeId !== storeId) return;
      dispatch(setLoadListingsProcessStatusFromSocket(payload));
    };
    const onStatus = (payload) => {
      if (payload?.storeId && payload.storeId !== storeId) return;
      dispatch(getLoadListingsStatusApi(storeId));
    };

    socketLoadListings.on('load-listings-run-state', onRunState);
    socketLoadListings.on('load-listings-status', onStatus);

    return () => {
      socketLoadListings.off('load-listings-run-state', onRunState);
      socketLoadListings.off('load-listings-status', onStatus);
      socketLoadListings.disconnect();
    };
  }, [dispatch, storeId]);

  const displayIsRunning =
    isLoadListingsRunning || Boolean(loadListingsProcessStatus?.isRunning);

  useEffect(() => {
    if (!displayIsRunning || !storeId) return undefined;
    const interval = setInterval(() => {
      dispatch(getLoadListingsStatusApi(storeId));
    }, 2000);
    return () => clearInterval(interval);
  }, [dispatch, storeId, displayIsRunning]);

  const steps = useMemo(
    () => mapRunStepsToUi(loadListingsProcessStatus),
    [loadListingsProcessStatus],
  );
  const overallProgress = loadListingsProcessStatus?.overallProgress ?? 0;
  const runSummary = loadListingsProcessStatus?.runSummary ?? null;
  const hasReport = Boolean(loadListingsProcessStatus?.hasReport);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
  };

  const handleAmazonSellerIdChange = (value) => {
    setAmazonSellerId(value);
    const updatedStore = {
      ...storeData,
      marketplace: {
        ...storeData.marketplace,
        configuration: {
          ...storeData.marketplace.configuration,
          sellerId: value,
        },
      },
    };
    onStoreUpdate(updatedStore);
  };

  const handleAmazonMarketplaceIdChange = (value) => {
    setAmazonMarketplaceId(value);
    const updatedStore = {
      ...storeData,
      marketplace: {
        ...storeData.marketplace,
        configuration: {
          ...storeData.marketplace.configuration,
          marketplaceId: value,
        },
      },
    };
    onStoreUpdate(updatedStore);
  };

  const handleLoadListings = async () => {
    if (!storeId) return;

    try {
      await dispatch(loadListingsApi(storeId)).unwrap();
      dispatch(getLoadListingsStatusApi(storeId));
    } catch (error) {
      console.error('Load listings failed:', error);
    }
  };

  const handleSaveSchedule = (schedule) => {
    if (!storeId) return;
    dispatch(
      updateLoadListingsScheduleApi({
        storeId,
        schedule,
      }),
    ).then((result) => {
      if (updateLoadListingsScheduleApi.fulfilled.match(result)) {
        onScheduleModalClose();
      }
    });
  };

  const listingFetchSchedule = storeData?.listingFetchSchedule;
  const scheduleIsEnabled = Boolean(listingFetchSchedule?.isEnabled);

  const isLoadingListings = loadListingsStatus === 'pending';
  const isSavingSchedule = updateLoadListingsScheduleStatus === 'pending';

  return (
    <Card
      title="Store Settings"
      rightElement={
        <Badge
          bg="blue.100"
          color="blue.700"
          fontSize="md"
          px={4}
          py={2}
          borderRadius="full"
        >
          Listing: {storeData?.listingsCount || 'None'}
        </Badge>
      }
    >
      <VStack spacing={6} align="stretch">
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack spacing={2}>
              <Text fontWeight="bold">Load Listings</Text>
              {scheduleIsEnabled && (
                <Badge colorScheme="green" variant="subtle" fontSize="xs">
                  Scheduled
                </Badge>
              )}
            </HStack>
            <Tooltip label="Daily load schedule" hasArrow>
              <IconButton
                aria-label="Daily load schedule settings"
                icon={<TimeIcon />}
                size="md"
                variant={scheduleIsEnabled ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={onScheduleModalOpen}
              />
            </Tooltip>
          </Flex>

          <HStack mb={4}>
            <Button
              colorScheme="blue"
              size="md"
              flex={1}
              leftIcon={<Icon as={DownloadIcon} />}
              onClick={handleLoadListings}
              isLoading={isLoadingListings || displayIsRunning}
              loadingText="Loading..."
              isDisabled={isLoadingListings || displayIsRunning}
            >
              Load Listings
            </Button>
          </HStack>

          <LoadListingsScheduleModal
            isOpen={isScheduleModalOpen}
            onClose={onScheduleModalClose}
            initialSchedule={listingFetchSchedule}
            onSave={handleSaveSchedule}
            isSaving={isSavingSchedule}
          />

          <Box mb={4}>
            <RunProgressPanel
              title="Load listings progress"
              embedded
              steps={steps}
              overallProgress={overallProgress}
              isRunning={displayIsRunning}
              currentMessage={loadListingsProcessStatus?.message ?? ''}
              runSummary={runSummary}
              summaryStats={LOAD_LISTINGS_SUMMARY_STATS}
              hasReport={hasReport}
              onDownloadReport={() => dispatch(downloadLoadListingsRunReportCsvApi(storeId))}
              isDownloadingReport={downloadLoadListingsReportStatus === 'loading'}
              showSummary={
                !displayIsRunning &&
                runSummary &&
                (runSummary.totalListingsFound ?? 0) > 0
              }
            />
          </Box>

          <HStack spacing={4} align="stretch">
            <FormControl maxW="48%" w="48%">
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none" h="full">
                  <Icon as={HiUpload} color="gray.400" />
                </InputLeftElement>
                <Input
                  type="file"
                  pt={2}
                  pl={10}
                  accept=".csv"
                  onChange={handleFileChange}
                  color={textColor}
                  value={selectedFile ? '' : undefined}
                  sx={{
                    '::file-selector-button': {
                      display: 'none',
                    },
                    '&::placeholder': {
                      color: textColor,
                    },
                  }}
                  cursor="pointer"
                  placeholder="Select CSV file..."
                  _placeholder={{ color: textColor }}
                />
                {selectedFile && (
                  <InputRightElement>
                    <IconButton
                      icon={<CloseIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={handleClearFile}
                      aria-label="Clear file"
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              {selectedFile && (
                <Text fontSize="sm" color={textColor} mt={1}>
                  {selectedFile.name}
                </Text>
              )}
            </FormControl>

            <Button
              variant="outline"
              colorScheme="blue"
              size="md"
              leftIcon={<DownloadIcon />}
              maxW="48%"
              w="48%"
            >
              Download File Format
            </Button>
          </HStack>
        </Box>

        {storeData?.marketplace?.name === 'amazon' && (
          <>
            <Divider />
            <Box>
              <Text fontWeight="bold" mb={4}>
                Marketplace Settings
              </Text>

              <HStack spacing={4} align="stretch">
                <FormControl>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color={textColor}>
                    Amazon Seller ID
                  </Text>
                  <Input
                    value={amazonSellerId}
                    onChange={(e) => handleAmazonSellerIdChange(e.target.value)}
                    placeholder="Enter Amazon Seller ID"
                    size="md"
                    color={inputColor}
                    bg={inputBgColor}
                    borderColor={borderColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </FormControl>

                <FormControl>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color={textColor}>
                    Amazon Marketplace ID
                  </Text>
                  <Input
                    value={amazonMarketplaceId}
                    onChange={(e) => handleAmazonMarketplaceIdChange(e.target.value)}
                    placeholder="Enter Amazon Marketplace ID"
                    size="md"
                    color={inputColor}
                    bg={inputBgColor}
                    borderColor={borderColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </FormControl>
              </HStack>
            </Box>
          </>
        )}
      </VStack>
    </Card>
  );
};

const StoreApiAuthAndSyncCard = ({ storeData, onStoreUpdate }) => {
  const [isSyncEnabled, setIsSyncEnabled] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenLoginUrl, setTokenLoginUrl] = useState(null);
  const [manualRefreshToken, setManualRefreshToken] = useState('');

  // Check if marketplace is eBay
  const isEbayStore = storeData?.marketplace?.name?.toLowerCase() === 'ebay';

  useEffect(() => {
    if (storeData) {
      setIsSyncEnabled(storeData.isSyncEnabled || false);
      setIsTokenValid(storeData.authentication?.isRefreshTokenValid || false);
      setTokenLoginUrl(storeData.tokenLoginUrl || null);

      // For non-eBay stores, populate manual token field if it exists
      if (!isEbayStore && storeData.authentication?.refreshToken) {
        setManualRefreshToken(storeData.authentication.refreshToken);
      }
    }
  }, [storeData, isEbayStore]);

  const handleSyncToggle = () => {
    const newSyncState = !isSyncEnabled;
    setIsSyncEnabled(newSyncState);

    const updatedStore = {
      ...storeData,
      isSyncEnabled: newSyncState,
    };
    onStoreUpdate(updatedStore);
  };

  const handleManualTokenChange = (e) => {
    const tokenValue = e.target.value;
    setManualRefreshToken(tokenValue);

    // Update store data with manual refresh token
    const updatedStore = {
      ...storeData,
      authentication: {
        ...storeData.authentication,
        refreshToken: tokenValue,
        isRefreshTokenValid: tokenValue.trim().length > 0, // Simple validation
      },
    };
    onStoreUpdate(updatedStore);
  };

  return (
    <Card title="Auth & Sync Settings">
      <VStack spacing={6} align="stretch">
        <Box>
          <HStack justify="space-between" mb={4}>
            <Text fontWeight="bold">Store Sync</Text>
            <Badge
              colorScheme={isSyncEnabled ? 'green' : 'gray'}
              variant="subtle"
              fontSize="sm"
            >
              {isSyncEnabled ? 'SYNC ACTIVE' : 'SYNC INACTIVE'}
            </Badge>
          </HStack>
          <Button
            colorScheme={isSyncEnabled ? 'red' : 'green'}
            onClick={handleSyncToggle}
            size="md"
            w="full"
          >
            {isSyncEnabled ? 'Turn Off Syncing' : 'Turn On Syncing'}
          </Button>
        </Box>
        <Divider />
        <Box>
          <HStack justify="space-between" mb={4}>
            <Text fontWeight="bold">Store API Auth</Text>
            <Badge
              colorScheme={isTokenValid ? 'green' : 'red'}
              variant="subtle"
              fontSize="sm"
            >
              {isTokenValid ? 'TOKEN VALID' : 'TOKEN INVALID'}
            </Badge>
          </HStack>

          {isEbayStore ? (
            <Button
              colorScheme="blue"
              size="md"
              w="full"
              onClick={() => {
                if (tokenLoginUrl) {
                  window.open(tokenLoginUrl, '_blank');
                }
              }}
              isDisabled={!tokenLoginUrl}
            >
              Log In To Refresh Token
            </Button>
          ) : (
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Enter your marketplace refresh token manually:
              </Text>
              <Textarea
                value={manualRefreshToken}
                onChange={handleManualTokenChange}
                placeholder="Paste your refresh token here..."
                size="md"
                minH="120px"
                rows={5}
                resize="vertical"
                fontFamily="mono"
                fontSize="sm"
              />
              <Text fontSize="xs" color="gray.500">
                This token will be encrypted and stored securely.
              </Text>
            </VStack>
          )}
        </Box>
      </VStack>
    </Card>
  );
};

const DescriptionTemplateCard = ({ storeData }) => {
  const dispatch = useDispatch();
  const { uploadDescriptionTemplateStatus } = useSelector((state) => state.stores);

  const hasTemplate = storeData?.descriptionTemplate || false;
  const [selectedFile, setSelectedFile] = useState(null);
  const { textColor } = useColors();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !storeData?._id) return;

    try {
      await dispatch(
        uploadDescriptionTemplateApi({
          storeId: storeData._id,
          file: selectedFile,
        }),
      ).unwrap();

      // Clear the selected file after successful upload
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const isUploading = uploadDescriptionTemplateStatus === 'pending';

  return (
    <Card
      title="Description Template"
      rightElement={
        <Badge
          bg={hasTemplate ? 'green.100' : 'yellow.100'}
          color={hasTemplate ? 'green.700' : 'yellow.700'}
          fontSize="sm"
        >
          {hasTemplate ? 'TEMPLATE ACTIVE' : 'NO TEMPLATE'}
        </Badge>
      }
    >
      <VStack spacing={4} align="stretch">
        <HStack spacing={4} align="stretch">
          <FormControl maxW="48%" w="48%">
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none" h="full">
                <Icon as={HiUpload} color="gray.400" />
              </InputLeftElement>
              <Input
                type="file"
                pt={2}
                pl={10}
                accept=".html"
                onChange={handleFileChange}
                color={textColor}
                value={selectedFile ? '' : undefined}
                sx={{
                  '::file-selector-button': {
                    display: 'none',
                  },
                  '&::placeholder': {
                    color: textColor,
                  },
                }}
                cursor="pointer"
                placeholder="Select HTML template file..."
                _placeholder={{ color: 'gray.400' }}
                isDisabled={isUploading}
              />
              {selectedFile && (
                <InputRightElement>
                  <IconButton
                    icon={<CloseIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={handleClearFile}
                    aria-label="Clear file"
                    isDisabled={isUploading}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            {selectedFile && (
              <Text fontSize="sm" color={textColor} mt={1}>
                {selectedFile.name}
              </Text>
            )}
          </FormControl>
          <Button
            colorScheme="blue"
            size="md"
            flex={1}
            leftIcon={<Icon as={HiUpload} />}
            maxW="48%"
            onClick={handleUpload}
            isDisabled={!selectedFile || isUploading}
            isLoading={isUploading}
            loadingText="Uploading..."
          >
            Upload Template
          </Button>
        </HStack>

        {hasTemplate && (
          <Button variant="outline" colorScheme="blue" size="md" w="full">
            View Sample Template
          </Button>
        )}
      </VStack>
    </Card>
  );
};

const ConfigurationCards = ({
  storeData,
  onStoreUpdate,
  hasChanges,
  onSave,
  isLoading,
}) => {
  return (
    <Box>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ListingOptionsCard storeData={storeData} onStoreUpdate={onStoreUpdate} />
        <StoreApiAuthAndSyncCard storeData={storeData} onStoreUpdate={onStoreUpdate} />
        <DescriptionTemplateCard storeData={storeData} />
      </SimpleGrid>
      <SaveButton hasChanges={hasChanges} onSave={onSave} isLoading={isLoading} />
    </Box>
  );
};

export default ConfigurationCards;
